import { IResourceService } from "./resources.js";
import { buildModelProjection, type ModelProjection } from "./modelProjection.js";

/**
 * Shared registration for weakly-tracked "extras" discovered via the three
 * devtools hook (`Loader` / `AnimationMixer` constructor observes, three
 * r182+ — mrdoob/three.js#32616). Used by BOTH capture paths:
 * - chrome extension: client shim captures at document_start, chrome.ts calls in
 * - PWA: threeDevtoolsResourceObserver captures at editor boot
 */

/** file-extension → display format for loaded model files */
const EXT_FORMAT: Record<string, string> = {
    glb: "GLTF", gltf: "GLTF", vrm: "VRM", fbx: "FBX", obj: "OBJ", stl: "STL",
    usd: "USD", usdz: "USD", usda: "USD", usdc: "USD", ply: "PLY", dae: "DAE",
    drc: "DRC", "3dm": "3DM",
};

export function registerAnimationMixer(resources: IResourceService, mixer: object): void {
    let label = "AnimationMixer";
    try {
        // AnimationMixer.getRoot() is public API; the root's name is the most
        // useful display handle a mixer has. NOT prefixed with "Mixer (…)": the
        // row already states its type, and the icon distinguishes it from the
        // clips it shares the Animations section with.
        const root = (mixer as { getRoot?(): { name?: string } | undefined }).getRoot?.();
        if (root?.name) label = root.name;
    } catch { /* host object — stay defensive */ }
    // type: the host constructor name is minified — say what it is
    resources.register(mixer, "mixer", { label, type: "AnimationMixer" });
}

// Ties each model's PROJECTION to its root's lifetime — the same ephemeron the
// client used for the raw result, but the value is inert data (no parser /
// associations), so nothing heavy is pinned. One projection per root: reused on
// re-register (url upgrade), swept when the page drops the model root.
const _modelProjectionByRoot = new WeakMap<object, ModelProjection>();

/** The in-scene root of a load result (gltf.scene, or the Group for FBX/OBJ). */
function modelRootOf(result: unknown): object | null {
    const r = result as { scene?: { isObject3D?: boolean }; isObject3D?: boolean; scenes?: Array<{ isObject3D?: boolean }> };
    if (r?.scene?.isObject3D) return r.scene as object;
    if (r?.isObject3D) return r as object;
    if (Array.isArray(r?.scenes) && r.scenes[0]?.isObject3D) return r.scenes[0] as object;
    return null;
}

export function registerLoadedModel(resources: IResourceService, result: object, url?: string, size?: number): void {
    // load(url): the last segment is the FILE — keep it verbatim, extension
    // included. parse(data, path): three's path arg is only the asset
    // DIRECTORY (no filename exists at that call site) — keep it recognizable
    // and add the scene name. (When parse ran INSIDE load, the capture layer
    // upgrades the directory to the real file url and re-registers.)
    const cleaned = url ? decodeURIComponent(url.split("?")[0]) : "";
    let label = cleaned.split("/").pop() || "";
    if (!label && cleaned) {
        const sceneName = (result as { scene?: { name?: string } }).scene?.name;
        label = sceneName ? `${cleaned} (${sceneName})` : cleaned;
    }
    // format from the file extension; gltf wrappers are also identifiable by
    // shape (the plain-object constructor name "Object" says nothing)
    const ext = label.includes(".") ? label.split(".").pop()!.toLowerCase() : "";
    const wrapper = result as { parser?: unknown; asset?: unknown };
    const format = EXT_FORMAT[ext]
        ?? ((!!wrapper.parser && !!wrapper.asset) ? "GLTF" : "File");
    // file size: from the parsed buffer (capture layer), else best-effort
    // resource timing for the fetch (same-origin / TAO only)
    let byteSize = size;
    if (!byteSize && url && typeof performance !== "undefined" && typeof location !== "undefined") {
        try {
            const resolved = new URL(url, location.href).href;
            const timing = performance.getEntriesByName(resolved)
                .find((e): e is PerformanceResourceTiming => e.entryType === "resource");
            byteSize = timing?.decodedBodySize || undefined;
        } catch { /* relative-url resolution is best-effort */ }
    }
    // Register a PROJECTION (inert data), NOT the live gltf `result` — holding the
    // result pins parser.associations → the whole scene graph + GPU textures. One
    // projection per root, anchored by _modelProjectionByRoot to the root's
    // lifetime so the (weakly-registered) entry is swept when the model is dropped.
    const root = modelRootOf(result);
    let projection = root ? _modelProjectionByRoot.get(root) : undefined;
    if (!projection) {
        projection = buildModelProjection(result);
        if (root) _modelProjectionByRoot.set(root, projection);
    }
    resources.register(projection, "model", { label, type: format, byteSize });
    if (root && url && /\.(?:glb|gltf)(?:[?#]|$)/i.test(url) && URL.canParse(url, typeof location !== "undefined" ? location.href : undefined)) {
        const source = new URL(url, typeof location !== "undefined" ? location.href : undefined);
        if (source.protocol === "https:" || source.protocol === "http:") resources.recordModelSource(projection, root, source.href);
    }
    // origin = the registered model (the projection), so clips link back to it
    registerAnimationClips(resources, result, projection);
}

/**
 * The load result's AnimationClips. Registered HERE, from the result, because
 * clips are not reachable any other way: they hang off `gltf.animations` rather
 * than the object graph, so the scene traversal that finds meshes/materials/
 * textures never sees them — which is why a file with four clips listed one
 * "Animation" resource (its mixer) and the resource graph drew no animation link.
 *
 * Works in BOTH products: the devtools hook hands us the result even when the
 * HOST page did the loading. (Attributing an *edit* to a file still needs an open
 * file — see resolveSourceAsset — but listing them does not.)
 */
export function registerAnimationClips(resources: IResourceService, result: object, origin: object = result): void {
    const animations = (result as { animations?: unknown }).animations;
    if (!Array.isArray(animations)) return;
    for (let index = 0; index < animations.length; index++) {
        const clip = animations[index];
        if (!registerAnimationClip(resources, clip)) continue;
        // where it came from. The tracker records origins during its MODEL WALK,
        // which cannot reach clips (not in the object graph) — so record it here,
        // otherwise a clip has no link back to its file at all. `origin` is the
        // registered model resource (the projection), so the link resolves to it.
        resources.recordOrigin(clip as object, origin, `/animations/${index}`);
    }
}

/**
 * A single clip. Split out because clips reach us from two directions: a loaded
 * file's `animations` array (above) and the MIXER playing them — a mixer can
 * play a clip whose file we never saw load, and that clip must still be a
 * resource. Re-registering a known clip is a cheap dedup no-op.
 *
 * Returns false for anything that isn't a clip-shaped object.
 */
export function registerAnimationClip(resources: IResourceService, clip: unknown): boolean {
    if (!clip || typeof clip !== "object") return false;
    const named = clip as { name?: string };
    resources.register(clip as object, "clip", {
        label: named.name || "AnimationClip",
        type: "AnimationClip",
        byteSize: clipByteSize(clip as object),
    });
    return true;
}

/** Keyframe memory: the times + values buffers of every track. Without this an
 *  animation-heavy file under-reports — clips are often megabytes of curves. */
function clipByteSize(clip: object): number | undefined {
    const tracks = (clip as { tracks?: unknown }).tracks;
    if (!Array.isArray(tracks)) return undefined;
    let bytes = 0;
    for (const track of tracks) {
        const t = track as { times?: { byteLength?: number }; values?: { byteLength?: number } };
        bytes += t?.times?.byteLength ?? 0;
        bytes += t?.values?.byteLength ?? 0;
    }
    return bytes > 0 ? bytes : undefined;
}
